'use client'

import { BRAND_NAME, INIT_DATA_STORAGE_KEY } from '@/app/constants'
import useIdleLoading from '@/app/hooks/useIdleLoading'
import { UserData } from '@/app/types'
import { GoogleAnalytics } from '@next/third-parties/google'
import { useLocalStorage } from '@uidotdev/usehooks'
import dynamic from 'next/dynamic'
import { FC, memo, useEffect, useRef, useState } from 'react'
import { PixelScript } from './PixelScript'

interface Props {
	pageName: string
	siteLanguage: string
}

const RenderGA: FC<Props> = ({ pageName, siteLanguage }) => {
	const isIdle = useIdleLoading()
	const [isGALoaded, setIsGALoaded] = useState(false)
	const [userData] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const hasLoadedGA = useRef(false)

	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.dataLayer = window.dataLayer || []
			window.gtag =
				window.gtag ||
				function () {
					; (window.dataLayer = window.dataLayer || []).push(arguments)
				}

			window.gtag('consent', 'default', {
				functionality_storage: 'denied',
				security_storage: 'granted',
				ad_personalization: 'denied',
				ad_storage: 'denied',
				ad_user_data: 'denied',
				analytics_storage: 'denied',
				personalization_storage: 'denied',
				region: ['MD', 'FR', 'IL'],
			})

			window.gtag('set', 'ads_data_redaction', true)
			window.gtag('set', 'url_passthrough', false)
		}
	}, [])

	useEffect(() => {
		if (isIdle && !hasLoadedGA.current) {
			const checkGtagAndFireEvent = () => {
				if (
					typeof window !== 'undefined' &&
					typeof window.gtag !== 'undefined'
				) {
					setIsGALoaded(true)
				} else {
					setTimeout(checkGtagAndFireEvent, 100)
				}
			}

			checkGtagAndFireEvent()
		}
	}, [isIdle])

	useEffect(() => {
		if (isGALoaded && userData && !hasLoadedGA.current) {
			const { sessionID } = userData

			window.gtag('set', {
				user_id: sessionID,
				user_properties: {
					page_name: pageName,
					site_language: siteLanguage,
				},
			})

			// TODO: refactor in a way we generally emit GA events
			const fireEvent = () => {
				let eventName = `${pageName}_view`
				window.gtag('event', eventName, {
					app_name: BRAND_NAME,
					screen_name: pageName,
				})

				hasLoadedGA.current = true
			}

			if (window.requestIdleCallback) {
				window.requestIdleCallback(fireEvent)
			} else {
				setTimeout(fireEvent, 0)
			}
		}
	}, [isGALoaded, userData, pageName, siteLanguage])

	return (
		<>
			{isIdle && (
				<>
					{process.env.NEXT_PUBLIC_PIXEL_ID && (
						<PixelScript
							pixelId={process.env.NEXT_PUBLIC_PIXEL_ID as string}
							pageName={pageName}
						/>
					)}
					<GoogleAnalytics
						dataLayerName="dataLayer"
						gaId={process.env.NEXT_PUBLIC_GA_ID as string}
					/>
				</>
			)}
		</>
	)
}

export default dynamic(() => Promise.resolve(memo(RenderGA)), {
	ssr: false,
})
